import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSupportBotBootstrap,
  markSupportBotOpened,
  sendSupportBotMessage,
} from '../../services/api';
import './SupportBotWidget.css';
import SupportBotChatSurface from './SupportBotChatSurface';
import { useAuthSession } from '../../contexts/AuthSessionContext';

const MAX_HISTORY_MESSAGES = 20;
const DEFAULT_HEADER_SUBTITLE = 'Assistente de suporte';
const DEFAULT_TOGGLE_LABEL = 'ajuda?';
const PRODUCT_ROUTES = [
  { prefix: '/atendabot', productKey: 'atendabot' },
  { prefix: '/checklist', productKey: 'checklist' },
  { prefix: '/reservo', productKey: 'reservo' },
  { prefix: '/clube', productKey: 'clube_mesa' },
  { prefix: '/cv', productKey: 'cv' },
  { prefix: '/cardapio', productKey: 'cardapio' },
  { prefix: '/feedback-hero', productKey: 'feedback_hero' },
];
const THEMED_PRODUCTS = new Set(PRODUCT_ROUTES.map((route) => route.productKey));

const getProductKeyFromPathname = (pathname = '') => {
  for (const route of PRODUCT_ROUTES) {
    if (pathname === route.prefix || pathname.startsWith(`${route.prefix}/`)) {
      return route.productKey;
    }
  }
  return null;
};

const isPublicCardapioSlugPath = (pathname = '') => {
  const parts = String(pathname || '').split('/').filter(Boolean);
  return parts[0] === 'cardapio'
    && parts.length === 2
    && parts[1] !== 'tags'
    && parts[1] !== 'checkin';
};

const isExcludedPublicPath = (pathname = '') => {
  const publicPrefixes = [
    '/cv/vagas',
    '/cv/aplicar',
    '/cv/successo',
    '/cv/application-success',
    '/cv/status',
    '/reservar',
    '/reserva',
    '/checkin',
  ];
  return isPublicCardapioSlugPath(pathname)
    || publicPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const getHistoryStorageKey = (productKey) => `support-bot-history-${productKey}`;
const getOpenedStorageKey = (productKey, sessionScope) => (
  `support-bot-opened-${sessionScope}-${productKey}`
);
const getDisplayStorageKey = (productKey, sessionScope) => (
  `support-bot-display-${sessionScope}-${productKey}`
);
const DISPLAY_STATES = new Set(['open', 'collapsed', 'toggle']);

const toSafeString = (value) => (
  typeof value === 'string' ? value : String(value ?? '')
);

const normalizeMessagesFromStorage = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => ({
      id: item?.id || `msg-${index}-${Date.now()}`,
      role: item?.role === 'assistant' ? 'assistant' : 'user',
      content: toSafeString(item?.content).trim(),
      createdAt: item?.createdAt || new Date().toISOString(),
    }))
    .filter((item) => item.content)
    .slice(-MAX_HISTORY_MESSAGES);
};

const loadHistory = (productKey) => {
  if (!productKey) return [];
  try {
    const raw = sessionStorage.getItem(getHistoryStorageKey(productKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeMessagesFromStorage(parsed);
  } catch (_error) {
    return [];
  }
};

const saveHistory = (productKey, messages) => {
  if (!productKey) return;
  try {
    const payload = normalizeMessagesFromStorage(messages).slice(-MAX_HISTORY_MESSAGES);
    sessionStorage.setItem(getHistoryStorageKey(productKey), JSON.stringify(payload));
  } catch (_error) {
  }
};

const hasOpenedSession = (productKey, sessionScope) => {
  if (!productKey) return false;
  try {
    return sessionStorage.getItem(getOpenedStorageKey(productKey, sessionScope)) === '1';
  } catch (_error) {
    return false;
  }
};

const setOpenedSession = (productKey, sessionScope) => {
  if (!productKey) return;
  try {
    sessionStorage.setItem(getOpenedStorageKey(productKey, sessionScope), '1');
  } catch (_error) {
  }
};

const loadDisplaySession = (productKey, sessionScope) => {
  if (!productKey) return null;
  try {
    const value = sessionStorage.getItem(getDisplayStorageKey(productKey, sessionScope));
    return DISPLAY_STATES.has(value) ? value : null;
  } catch (_error) {
    return null;
  }
};

const saveDisplaySession = (productKey, sessionScope, value) => {
  if (!productKey || !DISPLAY_STATES.has(value)) return;
  try {
    sessionStorage.setItem(getDisplayStorageKey(productKey, sessionScope), value);
  } catch (_error) {
  }
};

const asPreviewHistory = (messages = []) => (
  normalizeMessagesFromStorage(messages)
    .slice(-12)
    .map((item) => ({ role: item.role, content: item.content }))
);

const createMessage = (role, content) => ({
  id: `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role,
  content: toSafeString(content).trim(),
  createdAt: new Date().toISOString(),
});
const toThemeClassName = (productKey) => `support-bot-widget--${String(productKey || '').replace(/_/g, '-')}`;
const THEME_CONFIG_CSS_VAR_MAP = {
  panel_header_bg: '--sb-panel-header-bg',
  panel_header_text: '--sb-panel-header-text',
  avatar_bg: '--sb-avatar-bg',
  assistant_bg: '--sb-assistant-bg',
  assistant_text: '--sb-assistant-text',
  user_bg: '--sb-user-bg',
  user_text: '--sb-user-text',
  send_bg: '--sb-send-bg',
  send_text: '--sb-send-text',
  toggle_bg: '--sb-toggle-bg',
  toggle_text: '--sb-toggle-text',
  sliver_bg: '--sb-sliver-bg',
};

const normalizeThemeConfigInput = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.keys(THEME_CONFIG_CSS_VAR_MAP).reduce((acc, key) => {
    const raw = toSafeString(value[key]).trim();
    if (raw) acc[key] = raw;
    return acc;
  }, {});
};

const SupportBotWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loadingBootstrap, setLoadingBootstrap] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [productKey, setProductKey] = useState(null);
  const [botName, setBotName] = useState('Assistente');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [themeConfig, setThemeConfig] = useState({});
  const [headerSubtitle, setHeaderSubtitle] = useState(DEFAULT_HEADER_SUBTITLE);
  const [toggleLabel, setToggleLabel] = useState(DEFAULT_TOGGLE_LABEL);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [errorText, setErrorText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const panelBodyRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const { isAuthenticated, isAdmin, user } = useAuthSession();
  const sessionScope = user?.id ? `user-${user.id}` : 'authenticated';

  const visibleProductKey = useMemo(
    () => getProductKeyFromPathname(location.pathname),
    [location.pathname]
  );

  const shouldHideByPublicPath = useMemo(
    () => isExcludedPublicPath(location.pathname),
    [location.pathname]
  );
  const canShowWidget = Boolean(isAuthenticated && visibleProductKey && !shouldHideByPublicPath);
  const hasTheme = useMemo(
    () => Boolean(productKey && THEMED_PRODUCTS.has(productKey)),
    [productKey]
  );
  const themeClassName = useMemo(
    () => (hasTheme ? toThemeClassName(productKey) : ''),
    [hasTheme, productKey]
  );
  const themeStyle = useMemo(() => {
    const acc = Object.entries(THEME_CONFIG_CSS_VAR_MAP).reduce((styleAcc, [configKey, cssVar]) => {
      const value = toSafeString(themeConfig?.[configKey]).trim();
      if (value) styleAcc[cssVar] = value;
      return styleAcc;
    }, {});

    acc['--sb-widget-bottom'] = isAdmin ? '84px' : '20px';
    acc['--sb-widget-sliver-bottom'] = isAdmin ? '84px' : '20px';
    return acc;
  }, [themeConfig, isAdmin]);

  const scrollToBottom = useCallback(() => {
    const node = panelBodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom();
  }, [messages, isOpen, scrollToBottom]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!canShowWidget || !visibleProductKey) {
      setEnabled(false);
      setProductKey(null);
      setBotName('Assistente');
      setAvatarUrl('');
      setThemeConfig({});
      setHeaderSubtitle(DEFAULT_HEADER_SUBTITLE);
      setToggleLabel(DEFAULT_TOGGLE_LABEL);
      setMessages([]);
      setUnreadCount(0);
      setIsOpen(false);
      setIsCollapsed(false);
      return;
    }

    let cancelled = false;
    const currentProductKey = visibleProductKey;
    const rememberedDisplay = loadDisplaySession(currentProductKey, sessionScope);
    const openedThisSession = hasOpenedSession(currentProductKey, sessionScope);
    setLoadingBootstrap(true);
    setErrorText('');
    setProductKey(currentProductKey);
    const rememberedOpen = rememberedDisplay === 'open';
    setIsOpen(rememberedOpen);
    isOpenRef.current = rememberedOpen;
    setIsCollapsed(rememberedDisplay === 'collapsed');

    const existingHistory = loadHistory(currentProductKey);
    setMessages(existingHistory);
    setUnreadCount(0);

    (async () => {
      try {
        const bootstrap = await getSupportBotBootstrap(currentProductKey);
        if (cancelled) return;

        const isEnabled = Boolean(bootstrap?.enabled);
        setEnabled(isEnabled);
        if (!isEnabled) {
          setIsOpen(false);
          setIsCollapsed(false);
          return;
        }

        const presentation = bootstrap?.presentation_config || {};
        const nextBotName = toSafeString(presentation.bot_name || 'Assistente').trim() || 'Assistente';
        setBotName(nextBotName);
        setAvatarUrl(toSafeString(presentation.avatar_url || '').trim());
        setThemeConfig(normalizeThemeConfigInput(presentation.theme_config));
        setHeaderSubtitle(
          toSafeString(presentation.header_subtitle || DEFAULT_HEADER_SUBTITLE).trim() || DEFAULT_HEADER_SUBTITLE,
        );
        setToggleLabel(
          toSafeString(presentation.toggle_label || DEFAULT_TOGGLE_LABEL).trim() || DEFAULT_TOGGLE_LABEL,
        );
        setWelcomeMessage(toSafeString(bootstrap?.welcome_message || '').trim());
        setSuggestedQuestions(Array.isArray(bootstrap?.suggested_questions) ? bootstrap.suggested_questions : []);

        let nextMessages = existingHistory;
        if (
          bootstrap?.should_send_welcome
          && bootstrap?.welcome_message
          && !openedThisSession
        ) {
          const welcome = createMessage('assistant', bootstrap.welcome_message);
          nextMessages = [...existingHistory, welcome].slice(-MAX_HISTORY_MESSAGES);
          setMessages(nextMessages);
          saveHistory(currentProductKey, nextMessages);
          setUnreadCount((prev) => prev + (isOpenRef.current ? 0 : 1));
        }

        if (
          bootstrap?.auto_open_on_first_access
          && bootstrap?.should_send_welcome
          && !openedThisSession
          && !rememberedDisplay
        ) {
          setIsCollapsed(false);
          setIsOpen(true);
          isOpenRef.current = true;
          setUnreadCount(0);
          setOpenedSession(currentProductKey, sessionScope);
          saveDisplaySession(currentProductKey, sessionScope, 'open');
          try {
            await markSupportBotOpened(currentProductKey);
          } catch (_error) {
          }
        } else {
          const shouldRemainOpen = rememberedDisplay === 'open';
          setIsOpen(shouldRemainOpen);
          isOpenRef.current = shouldRemainOpen;
          setIsCollapsed(rememberedDisplay !== 'toggle' && !shouldRemainOpen);
        }
      } catch (error) {
        if (cancelled) return;
        setEnabled(false);
        setIsOpen(false);
        setIsCollapsed(false);
        setErrorText(error?.response?.data?.error || 'Não foi possível inicializar o chat de suporte.');
      } finally {
        if (!cancelled) setLoadingBootstrap(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canShowWidget, sessionScope, visibleProductKey]);

  const openChat = useCallback(async () => {
    setIsCollapsed(false);
    setIsOpen(true);
    setUnreadCount(0);
    saveDisplaySession(productKey, sessionScope, 'open');
    if (productKey && !hasOpenedSession(productKey, sessionScope)) {
      setOpenedSession(productKey, sessionScope);
      try {
        await markSupportBotOpened(productKey);
      } catch (_error) {
      }
    }
  }, [productKey, sessionScope]);

  const handleCollapse = useCallback(() => {
    setIsOpen(false);
    setIsCollapsed(true);
    saveDisplaySession(productKey, sessionScope, 'collapsed');
  }, [productKey, sessionScope]);

  const handleRestoreToggle = useCallback(() => {
    setIsOpen(false);
    setIsCollapsed(false);
    saveDisplaySession(productKey, sessionScope, 'toggle');
  }, [productKey, sessionScope]);

  const appendAssistantMessage = useCallback((text) => {
    if (!productKey) return;
    setMessages((prev) => {
      const next = [...prev, createMessage('assistant', text)].slice(-MAX_HISTORY_MESSAGES);
      saveHistory(productKey, next);
      return next;
    });
    if (!isOpen) setUnreadCount((prev) => prev + 1);
  }, [productKey, isOpen]);

  const handleSend = useCallback(async (textValue) => {
    if (!enabled || !productKey) return;
    const text = toSafeString(textValue).trim();
    if (!text || sending) return;

    const userMessage = createMessage('user', text);
    const nextMessages = [...messages, userMessage].slice(-MAX_HISTORY_MESSAGES);
    setMessages(nextMessages);
    saveHistory(productKey, nextMessages);
    setDraft('');
    setSending(true);
    setErrorText('');

    try {
      const data = await sendSupportBotMessage(productKey, {
        message: text,
        history: asPreviewHistory(nextMessages),
      });
      const responseText = toSafeString(data?.response).trim() || 'Não consegui responder agora.';
      appendAssistantMessage(responseText);
    } catch (error) {
      setErrorText(error?.response?.data?.error || 'Falha ao enviar mensagem para o bot.');
    } finally {
      setSending(false);
    }
  }, [enabled, productKey, sending, messages, appendAssistantMessage]);

  const handleSuggestedQuestion = (question) => {
    handleSend(question);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    handleSend(draft);
  };

  if (!canShowWidget || !enabled || shouldHideByPublicPath || !hasTheme) return null;

  return (
    <SupportBotChatSurface
      isOpen={isOpen}
      onToggle={openChat}
      onOpen={openChat}
      onCollapse={handleCollapse}
      onRestore={handleRestoreToggle}
      loading={loadingBootstrap}
      sending={sending}
      errorText={errorText}
      welcomeMessage={welcomeMessage}
      messages={messages}
      suggestedQuestions={suggestedQuestions}
      showSuggestedQuestionsOnlyAtStart
      onSuggestedQuestion={handleSuggestedQuestion}
      draft={draft}
      onDraftChange={setDraft}
      onSubmit={handleSubmit}
      unreadCount={unreadCount}
      botName={botName}
      avatarUrl={avatarUrl}
      headerSubtitle={headerSubtitle}
      toggleLabel={toggleLabel}
      closedDisplayMode={isCollapsed ? 'sliver' : 'toggle'}
      hideOpenToggle
      showClosedDismissControl
      themeClassName={themeClassName}
      themeStyle={themeStyle}
      messagesContainerRef={panelBodyRef}
    />
  );
};

export default SupportBotWidget;
